import {Menu} from "@headlessui/react";
import {useState, useEffect, useMemo} from "react";
import {BsFilter, BsSearch} from "react-icons/bs";
import {TableData} from "../../../components/TableData";
import {StatusCard} from "../../../components/StatusCard";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {EmptyState} from "../../../components/EmptyState";
import axios from "axios";
import {useQuery} from "react-query";
import debounce from "lodash.debounce";
import {getStatusType} from "../../../utils/status";
import toast from "react-hot-toast";

const ORDER_TYPE = [
  {label: "All", value: ""},
  {label: "Inter State", value: "INTER_STATE"},
  {label: "Domestic", value: "DOMESTIC"},
  {label: "International", value: "INTERNATIONAL"},
];

const TABLE_HEAD = [
  "",
  "Sender",
  "Recipient",
  "Waybill No",
  "Phone number",
  "Delivery Address",
  "City",
  "Delivery Date",
  "Delivery Type",
  "Status",
];

export const UnassignedDeliveriesTable = ({
  selectedDeliveries,
  setSelectedDeliveries,
}: {
  selectedDeliveries: any[];
  setSelectedDeliveries: React.Dispatch<React.SetStateAction<any[]>>;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const Baseurl = process.env.REACT_APP_BASEURL;
  const token = JSON.parse(localStorage.getItem("eftd-admin-token") || "{}")
    ?.data?.data?.token;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("Filter");
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState("");
  const [limit, setLimit] = useState(10);

  // Get current page from URL or default to 1
  const queryParams = new URLSearchParams(location.search);
  const currentPage = Number(queryParams.get("page")) || 1;

  const config = {
    headers: {Authorization: `Bearer ${token}`},
  };

  const handleSelectDelivery = (value: any) => {
    const type = value.deliveryType;

    setSelectedDeliveries((prev) => {
      const hasDomestic = prev.some((v) => v.deliveryType === "DOMESTIC");
      const hasInterState = prev.some((v) => v.deliveryType === "INTER_STATE");
      const hasInternational = prev.some(
        (v) => v.deliveryType === "INTERNATIONAL"
      );

      const isSelected = prev.some(
        (v) => v.waybillNumber === value.waybillNumber
      );

      if (isSelected) {
        return prev.filter((v) => v.waybillNumber !== value.waybillNumber);
      }

      if (
        (hasDomestic && (type === "INTER_STATE" || type === "INTERNATIONAL")) ||
        ((hasInterState || hasInternational) && type === "DOMESTIC")
      ) {
        toast.error(
          "Cannot combine DOMESTIC with INTER_STATE or INTERNATIONAL."
        );
        return prev;
      }

      if (
        (type === "INTER_STATE" && hasInterState) ||
        (type === "INTERNATIONAL" && hasInternational)
      ) {
        toast.error(`Only one ${type} delivery can be selected at a time.`);
        return prev;
      }

      if (
        (type === "INTER_STATE" || type === "INTERNATIONAL") &&
        prev.length > 0
      ) {
        toast.error(
          "Only one INTER_STATE or INTERNATIONAL delivery can be selected at a time."
        );
        return prev;
      }

      return [...prev, value];
    });
  };

  const getDeliveries = async () => {
    let url = `${Baseurl}/api/admin/orders/deliveries?limit=${limit}&page=${currentPage}`;

    // Combine both filters into one parameter
    let filters = "status:UNASSIGNED";
    if (deliveryTypeFilter) {
      filters += `,deliveryType:${deliveryTypeFilter}`;
    }

    url += `&filter=${encodeURIComponent(filters)}`;

    if (searchQuery) {
      url += `&query=${encodeURIComponent(searchQuery)}`;
    }

    const response = await axios.get(url, config);
    return response?.data?.data.items;
  };

  const {
    data: deliveriesData,
    isLoading: isLoadingDeliveryData,
    refetch,
    isRefetching: isRefetchingDeliveryData,
  } = useQuery(
    [
      "UNASSIGNED_DELIVERIES",
      currentPage,
      searchQuery,
      limit,
      deliveryTypeFilter,
    ],
    getDeliveries,
    {
      keepPreviousData: true,
    }
  );

  // Filter the data based on deliveryTypeFilter
  //   const filteredDeliveries = useMemo(() => {
  //     if (!deliveriesData) return [];

  //     if (!deliveryTypeFilter) return deliveriesData;

  //     return deliveriesData.filter(
  //       (delivery: any) => delivery.deliveryType === deliveryTypeFilter
  //     );
  //   }, [deliveriesData, deliveryTypeFilter]);

  const containerStyle = {
    width: "100%",
    borderRadius: "8px",
    border: "1px solid #e4e4e7",
  };

  const tableRowStyle = {
    borderBottom: "1px solid #eaecf0",
  };

  // Debounced search
  const debouncedSearch = debounce((value: string) => {
    setSearchQuery(value);
  }, 500);

  useEffect(() => {
    refetch();
  }, [searchQuery, currentPage, limit, refetch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedSearch(value);
  };

  const handleDeliveryTypeFilter = (value: string, label: string) => {
    setDeliveryTypeFilter(value);
    setSelectedLabel(label === "All" ? "Filter" : label);
    handlePageChange(1); // Reset to first page when filter changes
  };

  const handlePageChange = (newPage: number) => {
    navigate(`?page=${newPage}`);
  };

  // Calculate total pages based on both queries
  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(deliveriesData?.meta?.total / limit) || 0);
  }, [deliveriesData]);

  return (
    <div style={containerStyle}>
      <div className="flex items-center justify-between !px-6 !py-4">
        <h3 className="!text-xl !font-bold text-gray-700">
          Unassigned Deliveries
        </h3>
        <div className="flex gap-1.5">
          {/* Search */}
          <div className="relative !h-12 !bg-gray-50 !border !rounded-[10px] !w-[428px] !border-gray-300">
            <input
              type="text"
              placeholder="Search your orders"
              className="w-full h-full focus:!outline-none !pl-14 placeholder:!text-black/30 !text-sm"
              onChange={handleSearchChange}
              defaultValue={searchQuery}
            />
            <span className="!absolute !top-3.5 !left-6">
              <BsSearch />
            </span>
          </div>

          {/* Filter Dropdown */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex gap-1 items-center !py-[13px] !px-[9px] !border-[1.15px] border-gray-300 !rounded-[6px]">
              <BsFilter />
              <span className="!text-gray-400 !text-sm !font-medium">
                {selectedLabel ? selectedLabel : "Filter"}
              </span>
            </Menu.Button>
            <Menu.Items className="absolute z-10 !mt-2 !bg-white !shadow-lg !rounded-md !p-2 !w-52">
              {ORDER_TYPE.map((option) => (
                <Menu.Item key={option.value}>
                  {({active}) => (
                    <button
                      className={`!block !w-full !text-left !px-3 !py-2 !text-sm ${
                        active ? "!bg-gray-100" : ""
                      }`}
                      onClick={() =>
                        handleDeliveryTypeFilter(option.value, option.label)
                      }
                    >
                      {option.label}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Menu>
        </div>
      </div>

      {isLoadingDeliveryData || isRefetchingDeliveryData ? (
        <div style={{padding: "56px 0"}}>
          <p>Loading...</p>
        </div>
      ) : deliveriesData && deliveriesData.length > 0 ? (
        <>
          <div className="!overflow-auto">
            <table className="!w-full !min-w-max !table-auto !text-left">
              <thead>
                <tr className="!border-b !border-b-[#eaecf0] !bg-[#f9fafb]">
                  {TABLE_HEAD.map((head) => (
                    <th
                      key={head}
                      className="!text-xs !font-medium !text-[#667085] !p-3"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {deliveriesData &&
                  deliveriesData.map((order: any) => {
                    const status = getStatusType(order.status);
                    const formattedDate = new Date(
                      order.deliveryDate
                    ).toLocaleDateString("en-US", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    });

                    return (
                      <tr
                        key={order.id}
                        style={tableRowStyle}
                        className="border-b border-gray-100 hover:bg-gray-100 transition-colors"
                        onClick={() => {
                          handleSelectDelivery(order);
                        }}
                      >
                        <td style={{padding: "24px 16px"}}>
                          <input
                            type="checkbox"
                            id={order.id}
                            name="user"
                            value={order.id}
                            className="w-4 h-4 accent-orange-500 border-gray-200"
                            checked={selectedDeliveries.includes(order)}
                            onClick={() => {
                              handleSelectDelivery(order);
                            }}
                            onClickCapture={() => handleSelectDelivery(order)}
                          />
                        </td>
                        <TableData content={order.senderName} />
                        <TableData content={order.recipientName} />
                        <TableData content={order.waybillNumber || "N/A"} />
                        <TableData content={order.phone} />
                        <td className="!text-[13px] !font-medium !text-gray-600 !py-5 !px-3 text-wrap max-w-[200px]">
                          {`${order.address}`}
                        </td>
                        <TableData
                          content={
                            order.city && order.city !== "" ? order.city : "N/A"
                          }
                        />
                        <TableData content={formattedDate} />
                        <TableData content={order.deliveryType} />
                        <td style={{padding: "20px 12px"}}>
                          <StatusCard title={status.title} type={status.type} />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Simplified Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="flex items-center gap-6">
              <p className="text-sm text-gray-500">
                Showing page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-500">Items per page:</p>
              <select
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                }}
                className="border border-gray-500 rounded py-1 px-2 text-sm text-gray-500"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="40">40</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </>
      ) : (
        <div style={{padding: "56px 0"}}>
          <EmptyState
            type="order"
            text={
              searchQuery
                ? "No deliveries match your search"
                : "No Deliveries Found"
            }
          />
        </div>
      )}
    </div>
  );
};
